import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, RefObject } from 'react';
import { Channel, ChannelFolder } from '../../types/channel';
import ChannelFolderBlock from './ChannelFolder';
import { useSlotReorder } from '../navigation/useSlotReorder';
import { applyChannelMove, channelMoveFromOrder, flatRowIds, folderBeforeId, folderRowId, type ChannelMove } from './channelDnd';

/** What a channel row needs to take part in drag-to-reorder (see useSlotReorder). */
export type ChannelRowDnd = {
  /** The element that moves. */
  wrapperRef: (el: HTMLElement | null) => void;
  wrapperStyle: CSSProperties;
  /** Pointer and keyboard handlers for the row's button. */
  buttonProps: React.ButtonHTMLAttributes<HTMLButtonElement>;
  dragging: boolean;
  /** True once for the click that ends a drag, so dropping a row doesn't also open the channel. */
  consumeClick: () => boolean;
};

function ChannelList({
  channels,
  folders,
  renderChannel,
  canManageFolders = false,
  onAddChannelToFolder,
  onChannelReorder,
  onFolderReorder,
  scrollRef,
}: {
  channels: Channel[];
  folders: ChannelFolder[];
  renderChannel: (ch: Channel, dnd: ChannelRowDnd) => React.ReactNode;
  canManageFolders?: boolean;
  onAddChannelToFolder?: (folderId: string) => void;
  onChannelReorder?: (channelId: string, targetFolderId: string | null, beforeChannelId: string | null) => void;
  onFolderReorder?: (folderId: string, beforeFolderId: string | null) => void;
  /** The scrolling container around the list, so dragging near its edge scrolls it. */
  scrollRef?: RefObject<HTMLElement | null>;
}) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  // A dropped channel is shown in its new place straight away; the server's answer replaces this when it arrives.
  const [optimistic, setOptimistic] = useState<ChannelMove | null>(null);
  const channelSig = channels.map((c) => `${c.id}:${c.folderId || ''}:${c.position ?? ''}`).join('|');
  const sigRef = useRef(channelSig);
  useEffect(() => {
    if (sigRef.current !== channelSig) { sigRef.current = channelSig; setOptimistic(null); }
  }, [channelSig]);
  useEffect(() => {
    if (!optimistic) return;
    const t = window.setTimeout(() => setOptimistic(null), 4000); // the move failed or never arrived: show the truth
    return () => window.clearTimeout(t);
  }, [optimistic]);

  const grouped = useMemo(() => {
    const byFolder = new Map<string, Channel[]>();
    const roots: Channel[] = [];
    for (const ch of channels) {
      const fid = ch.folderId || '';
      if (!fid) { roots.push(ch); continue; }
      if (!byFolder.has(fid)) byFolder.set(fid, []);
      byFolder.get(fid)!.push(ch);
    }
    // Keep intra-folder order stable (position desc, then name)
    byFolder.forEach((list, fid) => {
      byFolder.set(
        fid,
        list
          .slice()
          .sort((a, b) => (Number(b.position || 0) - Number(a.position || 0)) || (a.name || '').localeCompare(b.name || '')),
      );
    });
    return { roots, byFolder };
  }, [channels]);
  const view = useMemo(() => (optimistic ? applyChannelMove(grouped, optimistic) : grouped), [grouped, optimistic]);

  // Folders with no channels aren't shown.
  const visibleFolders = useMemo(
    () => folders
      .slice()
      .sort((a, b) => (Number(b.position || 0) - Number(a.position || 0)) || a.name.localeCompare(b.name))
      .filter((f) => (view.byFolder.get(f.id) || []).length > 0),
    [folders, view],
  );

  const flatIds = useMemo(() => flatRowIds(view, visibleFolders, collapsed), [view, visibleFolders, collapsed]);
  const flatIndex = useMemo(() => new Map(flatIds.map((id, i) => [id, i])), [flatIds]);

  // Channels: one drag over the whole flat list, so a channel can cross folder headers into another folder or the root.
  const channelDrag = useSlotReorder({
    axis: 'y',
    ids: flatIds,
    scrollRef,
    lift: 1.03,
    onReorder: (order, movedId) => {
      if (!onChannelReorder) return;
      const move = channelMoveFromOrder(order, movedId);
      setOptimistic(move);
      onChannelReorder(move.channelId, move.folderId, move.beforeId);
    },
  });

  // Folders: a separate drag over the folder blocks, which move together with their channels.
  const folderDrag = useSlotReorder({
    axis: 'y',
    ids: visibleFolders.map((f) => f.id),
    scrollRef,
    lift: 1.02,
    onReorder: (order, movedId) => { onFolderReorder?.(movedId, folderBeforeId(order, movedId)); },
  });

  const rowFor = (ch: Channel) => {
    const index = flatIndex.get(ch.id) ?? 0;
    const dnd: ChannelRowDnd = {
      wrapperRef: channelDrag.itemRef(index),
      wrapperStyle: channelDrag.itemStyle(index, ch.id),
      buttonProps: onChannelReorder ? channelDrag.itemProps(index, ch.id) : {},
      dragging: channelDrag.dragId === ch.id,
      consumeClick: channelDrag.consumeClick,
    };
    return <Fragment key={ch.id}>{renderChannel(ch, dnd)}</Fragment>;
  };

  return (
    <div className="space-y-1">
      {view.roots.map(rowFor)}
      {visibleFolders.map((f, folderIndex) => {
        const headerIndex = flatIndex.get(folderRowId(f.id)) ?? 0;
        const open = !collapsed[f.id];
        const headerProps = onFolderReorder ? folderDrag.itemProps(folderIndex, f.id) : {};
        return (
          <ChannelFolderBlock
            key={f.id}
            folder={f}
            open={open}
            onToggle={() => setCollapsed((c) => ({ ...c, [f.id]: open }))}
            canManage={canManageFolders}
            onAddChannel={onAddChannelToFolder}
            blockRef={folderDrag.itemRef(folderIndex)}
            blockStyle={folderDrag.itemStyle(folderIndex, f.id)}
            headerRef={channelDrag.itemRef(headerIndex)}
            headerStyle={channelDrag.itemStyle(headerIndex, folderRowId(f.id))}
            headerProps={{
              ...headerProps,
              onClick: () => { if (folderDrag.consumeClick()) return; setCollapsed((c) => ({ ...c, [f.id]: open })); },
            }}
          >
            {(view.byFolder.get(f.id) || []).map(rowFor)}
          </ChannelFolderBlock>
        );
      })}
    </div>
  );
}

export default ChannelList;
